// services.ts — the service registry: which product surfaces are switched
// on in this deployment, and what each one contributes to the shell.
//
// Why this exists: the UI was written against nayto-processor, which
// served every screen (home feed, live, reports, admin health/sources/…).
// That backend no longer runs. The backend that does run — transcriber-api
// (github.com/Dror-Bengal/nayto-translator, apps/api) — implements only
// auth, /api/me, /api/translations and /api/admin/users. Every screen
// stays in the repo so it can come back the day its backend does; this
// file decides which ones are reachable, so that no request ever leaves
// the browser toward an endpoint that doesn't exist.
//
// ─── Runtime configuration (F-01) ────────────────────────────────────
//
// The enabled set is no longer a compile-time constant. It arrives from
// `GET /api/services` → `{ services: string[] }`, polled by
// `ServicesProvider` (see ./services-context). Every function below takes
// that set as its first argument, so the whole registry stays a pure,
// node-testable module — no React, no DOM, no Firebase. Keep it that way.
//
// The one invariant that protects production: a deployment with no
// /api/services behaves identically to the pre-F-01 bundle. Every read
// there fails, and the provider settles on DEFAULT_SERVICES — the exact
// three services that shipped before this refactor.
//
// F-02 put a tolerance in front of that. A failed read does NOT switch
// anything off on its own: the config in force is kept and a counter
// goes up, and only SERVICES_FAILURE_TOLERANCE failures in a row drop
// the app back to DEFAULT_SERVICES. One good read forgives the streak.
// So a deployment that HAS the endpoint does not lose its screens to a
// single dropped request, while one that does not have it still reaches
// the same floor — from a cold start it is already there.
//
// Adding a brand-new service is still one row in SERVICES. Everything
// else (nav tabs, route guard, admin tabs, onboarding steps, settings
// sections) reads from here.

import type { UserRole } from '@nayto/auth';

// ─── Routes (moved here from AppShell so the guard is testable) ──────

export type Route =
  | 'onboarding'
  | 'home'
  | 'replies'
  | 'leaderboard'
  | 'control'
  | 'live'
  | 'competitors'
  | 'ideas'
  | 'settings'
  | 'admin'
  | 'messagebox'
  | 'stats'
  | 'translator'
  | 'post';

export const KNOWN_ROUTES: readonly Route[] = [
  'onboarding',
  'home',
  'replies',
  'leaderboard',
  'control',
  'live',
  'competitors',
  'ideas',
  'settings',
  'admin',
  'messagebox',
  'stats',
  'translator',
  'post',
];

export const isRoute = (s: string): s is Route => (KNOWN_ROUTES as readonly string[]).includes(s);

/** Where a signed-in user lands when there is nowhere better to go:
 * after onboarding, after a disabled/unauthorised route, or when the
 * persisted route is stale. Was 'home' while the monitoring product
 * ran; the translator is the product now. */
export const DEFAULT_ROUTE: Route = 'translator';

// ─── Routes that no longer exist ─────────────────────────────────────
//
// F-16 gave the leaderboard its own nav item and deleted what stood
// between it and the user: the `reports` library (whose last card it
// was) and the `report` host that rendered it by ReportKey. Both names
// can still arrive — from a `sa_route` a browser stored last week, a
// `?route=reports` share link, or a `?report=leaderboard` bookmark — and
// every one of them meant "the leaderboard", because by F-13 there was
// nothing else behind them.
//
// Mapped rather than dropped: dropping would send those users to the
// translator, which is the one place they were certainly not going.

export const LEGACY_ROUTE_ALIASES: Readonly<Record<string, Route>> = {
  reports: 'leaderboard',
  report: 'leaderboard',
};

/** `?report=<key>` values that used to open a report card, and where
 * each one lives now. */
export const LEGACY_REPORT_PARAMS: Readonly<Record<string, Route>> = {
  leaderboard: 'leaderboard',
};

/** A known route as-is, a retired one mapped forward, anything else
 * null. Shape only — reachability is still `resolveRoute`'s job. */
export function canonicalRoute(s: string | null | undefined): Route | null {
  if (s == null) return null;
  if (isRoute(s)) return s;
  return Object.hasOwn(LEGACY_ROUTE_ALIASES, s) ? LEGACY_ROUTE_ALIASES[s]! : null;
}

/**
 * The route a load's query string asks for, or null. `?report=` wins
 * over `?route=`, as it did when it set both the route and the report
 * card; today every `?report=` value names a route of its own.
 */
export function deepLinkRoute(params: Pick<URLSearchParams, 'get'>): Route | null {
  const report = params.get('report');
  if (report != null && Object.hasOwn(LEGACY_REPORT_PARAMS, report)) {
    return LEGACY_REPORT_PARAMS[report]!;
  }
  return canonicalRoute(params.get('route'));
}

/**
 * Rewrites a stored `sa_route` that names a retired route, once, before
 * the shell reads it. Returns what it did so the caller can test it.
 *
 * Without this the stored value fails `isRoute`, the storage hook falls
 * back to its default, and a user who left the app on the leaderboard
 * comes back to the translator.
 */
export function migrateStoredRoute(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  key = 'sa_route',
): Route | null {
  let stored: string | null;
  try {
    stored = storage.getItem(key);
  } catch {
    return null;
  }
  if (stored == null || isRoute(stored)) return null;
  const next = canonicalRoute(stored);
  if (!next) return null;
  try {
    storage.setItem(key, next);
  } catch {
    return null;
  }
  return next;
}

// ─── Role gating ─────────────────────────────────────────────────────
//
// Source of truth for which routes a given role MAY navigate to,
// independent of which services are switched on. The visible set for a
// role is ROLE_ROUTES[role] ∩ enabled routes (see visibleRoutesFor).
// `post` is a detail route of Home; it is listed per role like any
// other, and reachable only when `home` is enabled.
//
// F-01 removed `gray_user`: transcriber-api never issued it (it knows
// pending / user / manager / admin), and any legacy value arriving from
// an API is normalised to 'user' by `normalizeRole` in @nayto/auth.

export const ROLE_ROUTES: Record<UserRole, ReadonlySet<Route>> = {
  pending: new Set<Route>(),
  admin: new Set<Route>([
    'home',
    'live',
    'stats',
    'replies',
    'leaderboard',
    'control',
    'competitors',
    'ideas',
    'messagebox',
    'admin',
    'translator',
    'settings',
    'post',
  ]),
  manager: new Set<Route>([
    'home',
    'live',
    'stats',
    'replies',
    'leaderboard',
    'control',
    'competitors',
    'messagebox',
    'translator',
    'settings',
    'post',
  ]),
  // `ideas` is ADMIN ONLY, and not by choice of this file: the API's
  // GET /api/intelligence/ideas is `requireRole('admin')`, an exact
  // membership check, so a manager opening the screen would get 403 on
  // every request. A nav item that 403s for whoever clicks it is worse
  // than no nav item. (Verified against nayto-core's auth middleware on
  // branch b-18-ideas, not assumed from the ticket.)
  //
  // user does NOT see 'stats' — admin/manager do. The Stats screen
  // surfaces published-content metrics (Strong Armenia) or Dark
  // insights, both out of scope for a regular user role per the matrix.
  // 'translator' IS visible to user — Ticket C says login-gated, anyone
  // with login can submit and see their own jobs.
  user: new Set<Route>([
    'home',
    'live',
    'replies',
    'leaderboard',
    'competitors',
    'messagebox',
    'translator',
    'settings',
    'post',
  ]),
};

// ─── Registry ────────────────────────────────────────────────────────

export type ServiceId =
  | 'translator'
  | 'settings'
  | 'admin'
  | 'home'
  | 'live'
  | 'stats'
  | 'replies'
  | 'leaderboard'
  | 'competitors'
  | 'ideas'
  | 'messagebox'
  | 'monitoring';

export type AdminTabKey =
  | 'health'
  | 'sources'
  | 'users'
  | 'prompts'
  | 'services'
  | 'channels'
  | 'deliveries'
  | 'calendar';

/** Onboarding steps a service can append after the mandatory `signin`
 * step (e-mail + password; accounts are created in the Firebase Console). */
export type OnboardingExtraStep = 'socials' | 'reports';

/** Settings-screen blocks a service can switch on. */
export type SettingsSection = 'socialAccounts' | 'reportPrefs';

export interface ServiceDefinition {
  /** Top-level routes this service owns. A route is reachable only when
   * some enabled service lists it. Several services may own the same
   * route — the enabled set is their union. */
  routes: readonly Route[];
  /** Admin-panel tabs this service contributes. */
  adminTabs?: readonly AdminTabKey[];
  /** Extra onboarding steps this service adds after `signin`. */
  onboardingSteps?: readonly OnboardingExtraStep[];
  /** Settings-screen sections this service switches on. */
  settingsSections?: readonly SettingsSection[];
  /** Which backend must be up for the service to work (for humans;
   * surfaced in the README, not used by code). */
  backend: string;
}

/** Every service the codebase knows about — enabled or not. Order is
 * irrelevant; nav order lives in AppShell's allTabs. */
export const SERVICES: Record<ServiceId, ServiceDefinition> = {
  translator: {
    routes: ['translator'],
    backend: 'transcriber-api — POST/GET/DELETE /api/translations, /:id, /:id/retry',
  },
  settings: {
    routes: ['settings'],
    backend: 'transcriber-api — GET /api/me, PATCH /api/me/language',
  },
  // F-04 added the Services tab here rather than under `monitoring`:
  // switching services and schedulers on is how an owner brings the
  // monitoring product up in the first place, so it cannot be gated on
  // the thing it turns on. Its three endpoints answer 404 today and the
  // tab says so quietly — see screens/admin/ServicesTab.
  admin: {
    routes: ['admin'],
    adminTabs: ['users', 'services'],
    backend:
      'transcriber-api — /api/admin/users; nayto-core — /api/admin/services, /api/admin/schedulers, /api/admin/budget',
  },
  home: {
    routes: ['home', 'post'],
    backend: 'nayto-core — /api/intelligence, /api/targets feed',
  },
  live: {
    routes: ['live'],
    backend: 'nayto-core — /api/intelligence/events, /api/intelligence/digests',
  },
  stats: {
    routes: ['stats'],
    backend: 'nayto-core — /api/stats',
  },
  replies: {
    routes: ['replies'],
    backend: 'nayto-core — /api/intelligence/comment-replies',
  },
  // F-01 split `reports` in two so the leaderboard — the one report
  // whose frontend and backend already agree, and which touches no LLM —
  // could be switched on without dragging the others along. F-12 and
  // F-13 deleted everything it was being kept apart from.
  //
  // F-16 finished the job: one route, its own nav item. The library it
  // used to sit in had one card, which is a click spent on nothing.
  leaderboard: {
    routes: ['leaderboard'],
    backend: 'nayto-core — /api/leaderboards, /api/leaderboards/posts',
  },
  competitors: {
    routes: ['competitors'],
    backend: 'nayto-core — /api/intelligence/competitors, /api/intelligence/targets',
  },
  // F-19 wired this to B-18. The approval queue is the whole service:
  // the distribution worker only ever sends an `approved` idea, so
  // without a screen to press the button on, the column produces rows
  // nobody can release.
  ideas: {
    routes: ['ideas'],
    backend: 'nayto-core — /api/intelligence/ideas, /ideas/:id/approve, /ideas/:id/reject',
  },
  messagebox: {
    routes: ['messagebox'],
    backend: 'nayto-core — /api/messagebox',
  },
  // Monitoring has no route of its own: it is the set of cross-cutting
  // features (social handles, report cadence, party access, the admin
  // Health/Targets/Sources/Prompts tabs) that only make sense when the
  // scraping + analysis pipeline runs.
  // F-03 dropped the Targets tab with the layer that owned it: the
  // per-person "tracked accounts" cohort is not part of the plan the
  // diagram describes. Sources still covers every monitored feed.
  // F-18 gave monitoring its first route. The daily control report is
  // the pipeline's own output — it exists only where the scraper, the
  // message box and the judge run — so it belongs to the service that
  // gates them rather than to one of its own. ROLE_ROUTES keeps it to
  // admin and manager: the report names pages and quotes posts, which is
  // a management view of the campaign's own output, not a user one.
  //
  // F-07 put Channels and Deliveries here rather than under `admin`.
  // The rule that falls out of F-04: every nayto-core admin tab belongs
  // to `monitoring`, and `services` is the single exception because it
  // is the lever that turns nayto-core on in the first place. Delivery
  // channels are not a bootstrap lever — there is nothing to deliver
  // until the pipeline runs — so gating them on the pipeline is both
  // consistent and the safer default: production, which has no
  // /api/services and therefore no monitoring, gains no new tabs.
  monitoring: {
    routes: ['control'],
    adminTabs: ['health', 'sources', 'prompts', 'channels', 'deliveries', 'calendar'],
    onboardingSteps: ['socials', 'reports'],
    settingsSections: ['socialAccounts', 'reportPrefs'],
    backend:
      'nayto-core — /api/me/handles, /api/admin/sources, /api/admin/system-health, /api/admin/prompts, /api/admin/channels, /api/admin/deliveries, /api/intelligence/control, /api/admin/calendar',
  },
};

export const KNOWN_SERVICE_IDS: readonly ServiceId[] = Object.keys(SERVICES) as ServiceId[];

export const isServiceId = (s: string): s is ServiceId =>
  (KNOWN_SERVICE_IDS as readonly string[]).includes(s);

// ─── The config ──────────────────────────────────────────────────────

/** Which services this deployment has switched on, right now. */
export type ServicesConfig = ReadonlySet<ServiceId>;

/** The floor. What the app shows when `GET /api/services` is missing,
 * unreachable, slow or malformed — i.e. what production shows today.
 * Never widen this without a backend that can be trusted to answer. */
export const DEFAULT_SERVICE_IDS: readonly ServiceId[] = ['translator', 'settings', 'admin'];

export const DEFAULT_SERVICES: ServicesConfig = new Set<ServiceId>(DEFAULT_SERVICE_IDS);

/** How often the provider re-reads the config. */
export const SERVICES_POLL_INTERVAL_MS = 60_000;

/** Consecutive failed reads before the app drops to DEFAULT_SERVICES.
 *
 * F-01 shipped this as 1 — any single failure fell straight to the
 * floor. That is correct as a policy and wrong as a cadence: one 30s
 * hiccup in nayto-core pulled an enabled service out from under every
 * open tab and put it back a minute later. Three strikes at a 60s poll
 * means a service survives ~2 minutes of backend trouble and is still
 * switched off within ~3 minutes of a backend that has genuinely gone
 * away. Still fail-closed — just not twitchy. */
export const SERVICES_FAILURE_TOLERANCE = 3;

/**
 * Turns a `GET /api/services` body into a config, or null when the body
 * is not something we are willing to act on.
 *
 * Deliberately strict about the envelope and forgiving about its
 * contents: `{ services: [...] }` must be present and an array, but
 * unknown ids inside it are dropped rather than rejecting the whole
 * response — a backend that learns about a service this bundle doesn't
 * know yet should not blank the nav. An empty array IS a valid answer
 * ("switch everything off"), so it returns an empty set, not null.
 */
export function parseServicesResponse(raw: unknown): ServicesConfig | null {
  if (raw === null || typeof raw !== 'object') return null;
  const list = (raw as { services?: unknown }).services;
  if (!Array.isArray(list)) return null;
  const out = new Set<ServiceId>();
  for (const entry of list) {
    if (typeof entry === 'string' && isServiceId(entry)) out.add(entry);
  }
  return out;
}

/**
 * One config read. Returns null on ANY failure — network error, non-2xx
 * (404 included: the endpoint simply doesn't exist yet), non-JSON body,
 * wrong shape.
 *
 * This function does not decide what a failure MEANS; it only reports
 * one. The policy lives in `applyServicesRead` below: fail-closed, but
 * only after SERVICES_FAILURE_TOLERANCE consecutive failures.
 *
 * Takes the fetcher as an argument so this stays testable in vitest's
 * node environment, and so the caller can hand it a fetch that does NOT
 * sign the user out on 401 (see apiFetchQuiet in ./auth/session).
 */
export async function fetchServicesConfig(
  fetcher: (path: string) => Promise<Response>,
): Promise<ServicesConfig | null> {
  try {
    const res = await fetcher('/api/services');
    if (!res.ok) return null;
    return parseServicesResponse(await res.json());
  } catch {
    return null;
  }
}

/** What the provider carries between polls: the config in force, and
 * how many reads in a row have failed since the last good one. */
export interface ServicesReadState {
  config: ServicesConfig;
  consecutiveFailures: number;
}

export const INITIAL_SERVICES_STATE: ServicesReadState = {
  config: DEFAULT_SERVICES,
  consecutiveFailures: 0,
};

/**
 * Folds one read into the state. The whole failure policy, in one pure
 * function so it can be tested without React or a clock.
 *
 *   - A successful read replaces the config and clears the counter. One
 *     good answer is enough to forgive any streak.
 *   - A failed read increments the counter and KEEPS the current config
 *     until the streak reaches SERVICES_FAILURE_TOLERANCE, at which
 *     point the app drops to DEFAULT_SERVICES and stays there while the
 *     failures continue.
 *
 * From a cold start every path leads to DEFAULT_SERVICES anyway, which
 * is why production — where /api/services 404s forever — sees nothing.
 */
export function applyServicesRead(
  prev: ServicesReadState,
  next: ServicesConfig | null,
): ServicesReadState {
  if (next) return { config: next, consecutiveFailures: 0 };
  const consecutiveFailures = prev.consecutiveFailures + 1;
  return {
    config: consecutiveFailures >= SERVICES_FAILURE_TOLERANCE ? DEFAULT_SERVICES : prev.config,
    consecutiveFailures,
  };
}

// ─── Derived views ───────────────────────────────────────────────────

export function isServiceEnabled(config: ServicesConfig, id: ServiceId): boolean {
  return config.has(id);
}

function enabledDefinitions(config: ServicesConfig): ServiceDefinition[] {
  return KNOWN_SERVICE_IDS.filter((id) => config.has(id)).map((id) => SERVICES[id]);
}

/** Routes some enabled service owns. 'onboarding' is always reachable —
 * it is the auth gate, not a service. */
export function enabledRoutes(config: ServicesConfig): ReadonlySet<Route> {
  return new Set<Route>(['onboarding', ...enabledDefinitions(config).flatMap((d) => d.routes)]);
}

export function isRouteEnabled(config: ServicesConfig, route: Route): boolean {
  if (route === 'onboarding') return true;
  return enabledDefinitions(config).some((d) => d.routes.includes(route));
}

/** True when `s` is a known route AND enabled under this config. */
export function isEnabledRoute(config: ServicesConfig, s: string): s is Route {
  return isRoute(s) && isRouteEnabled(config, s);
}

/** Query params that pin a route on load (`?route=`, and the retired
 * `?report=` that old bookmarks still carry — see LEGACY_REPORT_PARAMS).
 * AppShell
 * applies them ONCE and then strips them from the URL: a share link or
 * bookmark that carries `?route=translator` must not keep dragging every
 * later refresh back to the translator (QA: "refresh on Settings lands on
 * the translator"). `?job=` is stripped by TranslatorScreen itself. */
export const ROUTE_DEEP_LINK_PARAMS: readonly string[] = ['route', 'report'];

/** Returns the href without the route deep-link params, or null when the
 * URL carried none (nothing to rewrite). Other params are kept as-is. */
export function stripRouteDeepLinkParams(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  let changed = false;
  for (const p of ROUTE_DEEP_LINK_PARAMS) {
    if (url.searchParams.has(p)) {
      url.searchParams.delete(p);
      changed = true;
    }
  }
  return changed ? url.toString() : null;
}

/** ROLE_ROUTES[role] ∩ enabled routes — what the nav shows and what the
 * guard allows for this role. */
export function visibleRoutesFor(config: ServicesConfig, role: UserRole): ReadonlySet<Route> {
  const out = new Set<Route>();
  for (const r of ROLE_ROUTES[role]) if (isRouteEnabled(config, r)) out.add(r);
  return out;
}

/** Where a requested route actually ends up. Disabled or unknown →
 * DEFAULT_ROUTE. When a role is given, routes the role may not see are
 * also redirected. 'onboarding' passes through untouched (the auth gate
 * owns it).
 *
 * AppShell calls this on every render against the LIVE config, so a
 * route that was enabled a minute ago and has since been switched off
 * falls back on the next poll without a reload. */
export function resolveRoute(
  config: ServicesConfig,
  requested: string | null | undefined,
  role?: UserRole,
): Route {
  if (!requested || !isRoute(requested)) return DEFAULT_ROUTE;
  if (requested === 'onboarding') return requested;
  if (!isRouteEnabled(config, requested)) return DEFAULT_ROUTE;
  if (role && !ROLE_ROUTES[role].has(requested)) return DEFAULT_ROUTE;
  return requested;
}

/** Admin-panel tabs, in the panel's canonical display order, filtered
 * to what enabled services contribute. */
const ADMIN_TAB_ORDER: readonly AdminTabKey[] = [
  'health',
  'sources',
  'users',
  'services',
  // Channels then Deliveries: you configure a channel, then you read
  // what it did. Reversing them would put the consequence before the
  // cause.
  'channels',
  'deliveries',
  // The calendar feeds the ideas generator, and Prompts is the other
  // thing that shapes what a model is told. They sit together.
  'calendar',
  'prompts',
];

export function enabledAdminTabs(config: ServicesConfig): readonly AdminTabKey[] {
  const contributed = new Set<AdminTabKey>(
    enabledDefinitions(config).flatMap((d) => d.adminTabs ?? []),
  );
  return ADMIN_TAB_ORDER.filter((k) => contributed.has(k));
}

/** Onboarding steps: the mandatory `signin` plus whatever enabled
 * services append, in the wizard's canonical order. Language is picked
 * in Settings, not in the wizard. */
export type OnboardingStep = 'signin' | OnboardingExtraStep;

const ONBOARDING_BASE_STEPS: readonly OnboardingStep[] = ['signin'];
const ONBOARDING_EXTRA_ORDER: readonly OnboardingExtraStep[] = ['socials', 'reports'];

export function enabledOnboardingSteps(config: ServicesConfig): readonly OnboardingStep[] {
  const contributed = new Set<OnboardingExtraStep>(
    enabledDefinitions(config).flatMap((d) => d.onboardingSteps ?? []),
  );
  return [...ONBOARDING_BASE_STEPS, ...ONBOARDING_EXTRA_ORDER.filter((s) => contributed.has(s))];
}

export function isSettingsSectionEnabled(
  config: ServicesConfig,
  section: SettingsSection,
): boolean {
  return enabledDefinitions(config).some((d) => d.settingsSections?.includes(section));
}
